import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, Icon } from '@hp/ui';
import type { Permission } from '@hp/domain';
import type { PipelineStage, PipelineStageStatus, PipelineState } from './queries';
import { ErrorAlert } from '../../components/error-alert';
import { usePipelineText } from './messages';
import { isNextStage, isRerunnable, KIND_PERMISSION, STAGE_TO_KIND } from './stage-meta';
import { usePipelineAction } from './use-pipeline-action';

// --- c) Pipeline visualization ---

function StageMark({ status }: { status: PipelineStageStatus }) {
  const { t } = useTranslation(['pipeline', 'common']);
  const cls = `bo-stage__mark bo-stage__mark--${status}`;
  const title = t(`mark.${status}`);
  switch (status) {
    case 'done':
      return (
        <span className={cls} title={title}>
          <Icon name="check" />
        </span>
      );
    case 'active':
      return (
        <span className={cls} title={title}>
          <Icon name="refresh" />
        </span>
      );
    case 'ready':
      return (
        <span className={cls} title={title}>
          <Icon name="play" />
        </span>
      );
    case 'blocked':
      return (
        <span className={cls} title={title}>
          <Icon name="lock" />
        </span>
      );
    case 'pending':
      return <span className={cls} title={title} />;
  }
}

function StageStartButton({
  stage,
  can,
  act,
}: {
  stage: PipelineStage;
  can: (permission: Permission) => boolean;
  act: ReturnType<typeof usePipelineAction>;
}) {
  const { t } = useTranslation(['pipeline', 'common']);
  // The server picks the sync flavour (full copy vs changes-only) — honour it.
  const kind = stage.actionKind ?? STAGE_TO_KIND[stage.key];
  const needed = KIND_PERMISSION[kind];
  if (needed !== undefined && !can(needed)) return null;

  if (stage.status === 'ready') {
    return (
      <HpButton
        variant="secondary"
        className="bo-button--sm"
        busy={act.isBusy({ kind, entityType: stage.entityType })}
        disabled={act.isPending}
        onClick={() => act.execute({ kind, entityType: stage.entityType })}
      >
        {t(`stageActions.${stage.key}`)}
      </HpButton>
    );
  }

  // Done data ages — offer a quiet refresh (dedup/enrichment re-runs never
  // overwrite human decisions; sync/backup are pull-only snapshots).
  if (stage.status === 'done' && isRerunnable(stage.key)) {
    return (
      <HpButton
        variant="quiet"
        className="bo-button--sm"
        busy={act.isBusy({ kind, entityType: stage.entityType })}
        disabled={act.isPending}
        onClick={() => act.execute({ kind, entityType: stage.entityType })}
      >
        {t(`rerun.${stage.key}`)}
      </HpButton>
    );
  }

  return null;
}

function StageRow({
  stage,
  state,
  can,
  act,
}: {
  stage: PipelineStage;
  state: PipelineState;
  can: (permission: Permission) => boolean;
  act: ReturnType<typeof usePipelineAction>;
}) {
  const { t, text } = usePipelineText();
  const next = isNextStage(state.nextAction, stage);
  const blocked = stage.blockedMsg || stage.blockedReason ? text(stage.blockedMsg, stage.blockedReason ?? '') : '';
  return (
    <li className={next ? 'bo-stage is-next' : 'bo-stage'} aria-current={next ? 'step' : undefined}>
      <StageMark status={stage.status} />
      <div>
        <div className="bo-stage__title">{t(`stages.${stage.key}`)}</div>
        <div className="bo-stage__detail">{text(stage.detailMsgs, stage.detail)}</div>
        {blocked && <div className="bo-stage__reason">{blocked}</div>}
      </div>
      <div className="bo-stage__action">
        <StageStartButton stage={stage} can={can} act={act} />
      </div>
    </li>
  );
}

export function PipelineViz({ state, can }: { state: PipelineState; can: (permission: Permission) => boolean }) {
  const { t, text } = usePipelineText();
  const act = usePipelineAction();
  const sharedStages = state.stages.filter((s) => s.entityType === undefined);
  return (
    <div className="bo-sections">
      <ErrorAlert error={act.error} />
      <div className="bo-pipeline">
        <section className="hp-card bo-pipeline--wide">
          <HpCardHeader title={t('viz.dataTitle')} subtitle={t('viz.dataSubtitle')} icon="database" />
          <ol className="bo-stage-list bo-stage-list--columns">
            {sharedStages.map((stage) => (
              <StageRow key={stage.key} stage={stage} state={state} can={can} act={act} />
            ))}
          </ol>
        </section>

        {(['organization', 'person'] as const).map((entity, i) => {
          const locked = entity === 'person' && state.personPhaseLocked;
          const stages = state.stages.filter((s) => s.entityType === entity);
          const entityTitle = entity === 'organization' ? t('viz.organizations') : t('viz.persons');
          return (
            <section
              key={entity}
              className={locked ? 'hp-card bo-pipeline__card is-locked' : 'hp-card bo-pipeline__card'}
            >
              <HpCardHeader
                title={`${i + 1} · ${entityTitle}`}
                icon={entity === 'organization' ? 'building' : 'users'}
              />
              {locked && state.personPhaseLockReason && (
                <p className="bo-pipeline__lock">
                  <Icon name="lock" />
                  {text(state.personPhaseLockMsg, state.personPhaseLockReason)}
                </p>
              )}
              <ol className="bo-stage-list">
                {stages.map((stage) => (
                  <StageRow key={stage.key} stage={stage} state={state} can={can} act={act} />
                ))}
              </ol>
            </section>
          );
        })}
      </div>
    </div>
  );
}
